// ---------------------------------------------------------------------------
// Selector visibility: the products, sets, backgrounds and layouts that an
// admin hid from the selectors (the `hidden*` fields of
// /api/v3/channel-settings).
//
// A hidden option is only not OFFERED. The Product and Set dropdowns, the
// background tiles and the layout buttons leave it out. Everything else keeps
// it: a link that names it opens it, a saved scene that uses it renders it,
// and the page that shows it keeps it as the current value. Only the code that
// CHOOSES for the user skips it while a visible option exists: the channel
// landing, the product switch, the opening background of a page without
// `?background=` and the opening layout of a page without `?layout=`.
//
// A hidden background is the exception: the page has no such background
// (Sergey, 2026-10-26). A link that names one opens the first tile, and so
// does a page whose current background an admin hides (layout/BgSelector).
// Only a saved scene keeps it.
//
// Products are hidden per channel, or per stage (content type): a product
// hidden on every stage it has leaves the Product dropdown, and a stage it is
// hidden on leaves its Stage dropdown. Product `*` on a stage hides the stage
// for every product. On a stage-typed channel (Releases) an admin can also put
// a product on a stage that the code does not give it (`shownStageProducts`):
// the stage then has the product as if the code listed it. Sets are hidden per
// channel, or per stage for one product or for every product (`*`).
//
// Backgrounds are hidden per gallery (stage and product). On releases, a
// background can also be hidden in one set only: each set then shows its own
// group of the product's backgrounds, and can have its own tile order
// (`setBackgroundOrders`). The built-in backgrounds (the generative gradient)
// are hidden in the same scopes as the catalog backgrounds. Layouts are hidden
// per stage, set and product, or per stage and set for every product (`*`).
//
// Layouts also have an order, per set and product or per set for every product
// (`layoutOrders`), for every stage. The Layout selector shows its buttons in
// that order, and the first offered layout is the default: the layout that
// code selects for the user (the channel landing, a stage or product switch,
// a page without `?layout=`).
//
// Pure module: the server imports it too (channel-settings.ts,
// default-scenes-resolved.ts).
// ---------------------------------------------------------------------------

import {findChannelObj, productCodesForType} from "../data/products.js";

/**
 * The backgrounds that code draws, with no catalog image: the generative
 * gradient. The selector shows a tile for each one after the catalog tiles.
 */
export const BUILT_IN_BACKGROUNDS = ["generative"] as const;

export type BuiltInBackground = (typeof BUILT_IN_BACKGROUNDS)[number];

/** A background of a gallery: a catalog variation or a built-in background. */
export type BackgroundId = number | BuiltInBackground;

export function isBuiltInBackground(value: unknown): value is BuiltInBackground {
  return BUILT_IN_BACKGROUNDS.includes(value as BuiltInBackground);
}

/** The hidden lists of one channel, as GET /api/v3/channel-settings returns them. */
export interface HiddenSelectorOptions {
  /** Product codes that the channel's Product dropdown does not offer. */
  hiddenProducts: string[];
  /**
   * Stage (content type) → the product codes hidden on that stage. `*` hides
   * the stage for every product.
   */
  hiddenStageProducts: Record<string, string[]>;
  /**
   * Stage → the product codes that the stage has although the code does not
   * list them there (stage-typed channels only). A product the code lists
   * gets no mark here: it has a hidden mark or none.
   */
  shownStageProducts: Record<string, string[]>;
  /** Releases set values that the Set dropdown does not offer. */
  hiddenSets: string[];
  /**
   * `${type}:${product}`, with product `*` for every product → the releases
   * set values that the Set dropdown of that stage does not offer.
   */
  hiddenStageSets: Record<string, string[]>;
  /**
   * The same keys as `hiddenStageSets` → the set values that the Set dropdown
   * of that stage offers although a wider rule hides them. The narrowest
   * rule wins: channel, then the stage for every product, then the stage for
   * the product.
   */
  shownStageSets: Record<string, string[]>;
  /**
   * `${type}:${product}` (one gallery, every set) or
   * `${type}:${product}:${set}` (one releases set of it) → the variations
   * that get no tile.
   */
  hiddenBackgrounds: Record<string, number[]>;
  /**
   * The same keys as `hiddenBackgrounds` → the built-in backgrounds that get
   * no tile. A separate field: older code reads `hiddenBackgrounds` as
   * numbers only, and the database is shared with it.
   */
  hiddenBuiltInBackgrounds: Record<string, string[]>;
  /**
   * `${type}:${product}` or `${type}:${product}:${set}`, with product `*` for
   * every product → the layout ids that the Layout selector does not offer.
   */
  hiddenLayouts: Record<string, number[]>;
  /**
   * `${type}:${product}:${set}` → the tile order of the gallery in one
   * releases set. A set with no entry shows the product's order.
   */
  setBackgroundOrders: Record<string, number[]>;
  /**
   * `${product}`, or `${product}:${set}` on releases, with product `*` for
   * every product → the layout ids in the order of the Layout selector, for
   * every stage. A product with no order of its own uses the order for every
   * product.
   */
  layoutOrders: Record<string, number[]>;
}

/**
 * The hidden lists of a channel-settings response. The fields are optional:
 * a server from before this feature sends only `stageOrder` (the lightweight
 * dev server proxies to staging), and nothing is hidden then.
 */
export type ChannelSettingsLike = Partial<HiddenSelectorOptions> | null | undefined;

/** The product scope of a layout rule for every product. */
export const ALL_PRODUCTS_SCOPE = "*";

/** `type:product`, or `type:product:set` for a releases set. */
function scopeKey(type: string, product: string, set?: string | null): string {
  return set ? `${type}:${product}:${set}` : `${type}:${product}`;
}

/**
 * The key of one gallery in `hiddenBackgrounds`: for every set, or for one
 * releases set of the gallery.
 */
export function hiddenBackgroundsKey(type: string, product: string, set?: string | null): string {
  return scopeKey(type, product, set);
}

/** The key of one layout rule in `hiddenLayouts`. Product `*` is every product. */
export function hiddenLayoutsKey(type: string, product: string, set?: string | null): string {
  return scopeKey(type, product, set);
}

/** The key of one layout order in `layoutOrders`. Product `*` is every product. */
export function layoutOrderKey(product: string, set?: string | null): string {
  return set ? `${product}:${set}` : product;
}

/** The key of one stage rule in `hiddenStageSets`. Product `*` is every product. */
export function hiddenStageSetsKey(type: string, product: string): string {
  return `${type}:${product}`;
}

/** The key of one set's tile order in `setBackgroundOrders`. */
export function setBackgroundOrderKey(type: string, product: string, set: string): string {
  return `${type}:${product}:${set}`;
}

/** The products hidden in the whole channel, on every stage. */
export function hiddenProductsOf(settings: ChannelSettingsLike): Set<string> {
  return new Set(settings?.hiddenProducts ?? []);
}

/**
 * The stages on which `product` is not offered: the stages an admin hid it
 * on, and the stages hidden for every product. The channel-wide list is not
 * included (hiddenProductsOf).
 */
export function hiddenStagesOf(
  settings: ChannelSettingsLike,
  product: string | null | undefined
): Set<string> {
  const lists = settings?.hiddenStageProducts ?? {};
  return new Set(
    Object.entries(lists)
      .filter(([, codes]) => codes.includes(ALL_PRODUCTS_SCOPE) || (!!product && codes.includes(product)))
      .map(([type]) => type)
  );
}

/** True when the code (products.js) lists `product` on `stage` of the channel. */
export function isOnStageInCode(channel: string, stage: string, product: string): boolean {
  return (productCodesForType(channel, stage) ?? []).includes(product);
}

/**
 * True when `stage` has `product`: the code lists it there, or an admin put it
 * there (`shownStageProducts`, stage-typed channels only). A hidden mark does
 * not change this: offeredStages applies the hidden marks.
 */
export function stageHasProduct(
  settings: ChannelSettingsLike,
  channel: string,
  stage: string,
  product: string
): boolean {
  if (isOnStageInCode(channel, stage, product)) return true;
  if (!findChannelObj(channel)?.typesAreStages) return false;
  return (settings?.shownStageProducts?.[stage] ?? []).includes(product);
}

/**
 * The mark that a change of a product on one stage writes. A product that the
 * code lists there (or `*`, the stage itself) gets a hidden mark or none.
 * Another product gets a shown mark, which puts it on the stage, or none.
 */
export function stageProductMarkAfter(change: HiddenChange): "hidden" | "shown" | null {
  if (change.kind !== "product" || !change.type) {
    throw new Error("A stage product mark needs a product change with a type");
  }
  const value = String(change.value);
  if (value === ALL_PRODUCTS_SCOPE || isOnStageInCode(change.channel, change.type, value)) {
    return change.hidden ? "hidden" : null;
  }
  return change.hidden ? null : "shown";
}

/**
 * The stages of `stages` that offer `product`, in their order. When an admin
 * hid the product on each of them, all of them stay: code that must choose a
 * stage still has one. `keep` (the current stage) always stays.
 */
export function offeredStages(
  settings: ChannelSettingsLike,
  product: string | null | undefined,
  stages: string[],
  keep?: string | null
): string[] {
  const hidden = hiddenStagesOf(settings, product);
  if (hidden.size === 0) return stages;
  const offered = stages.filter((stage) => !hidden.has(stage) || stage === keep);
  return offered.some((stage) => !hidden.has(stage)) ? offered : stages;
}

/**
 * Does the Product dropdown offer `product`? Not when the channel hides it,
 * and not when every stage of `stages` hides it. A stage-typed channel
 * (Releases) passes each stage the product has: the dropdown offers the whole
 * channel, and a pick moves to a stage that offers it. A content-type channel
 * passes the current type. No stages: the channel-wide list decides.
 */
export function isProductOffered(
  settings: ChannelSettingsLike,
  product: string,
  stages: string[]
): boolean {
  if (hiddenProductsOf(settings).has(product)) return false;
  if (stages.length === 0) return true;
  const hidden = hiddenStagesOf(settings, product);
  return stages.some((stage) => !hidden.has(stage));
}

/**
 * The sets hidden on a page of (type, product). The narrowest rule that names
 * a set wins: the channel-wide list, then the rule of the stage for every
 * product, then the rule of the stage for this product. A stage rule can hide
 * a set or show it. Product `*` gives the sets of the stage for every
 * product; without a type, the channel-wide list only.
 */
export function hiddenSetsOf(
  settings: ChannelSettingsLike,
  type?: string | null,
  product?: string | null
): Set<string> {
  const hidden = new Set(settings?.hiddenSets ?? []);
  if (!type) return hidden;
  const keys = [hiddenStageSetsKey(type, ALL_PRODUCTS_SCOPE)];
  if (product && product !== ALL_PRODUCTS_SCOPE) keys.push(hiddenStageSetsKey(type, product));
  for (const key of keys) {
    for (const set of settings?.hiddenStageSets?.[key] ?? []) hidden.add(set);
    for (const set of settings?.shownStageSets?.[key] ?? []) hidden.delete(set);
  }
  return hidden;
}

/**
 * True when the rule above the stage rule (type, product) hides `set`: the
 * channel-wide list for product `*`, the stage for every product otherwise.
 */
export function isSetHiddenAbove(
  settings: ChannelSettingsLike,
  type: string,
  product: string,
  set: string
): boolean {
  const above =
    product === ALL_PRODUCTS_SCOPE
      ? hiddenSetsOf(settings)
      : hiddenSetsOf(settings, type, ALL_PRODUCTS_SCOPE);
  return above.has(set);
}

/**
 * The mark that a stage set change writes: none when the change gives the
 * state of the wider rule (the rule then follows it again), else a hidden or a
 * shown mark.
 */
export function stageSetMarkAfter(
  settings: ChannelSettingsLike,
  change: HiddenChange
): "hidden" | "shown" | null {
  if (change.kind !== "set" || !change.type) {
    throw new Error("A stage set mark needs a set change with a type");
  }
  const above = isSetHiddenAbove(
    settings,
    change.type,
    change.product ?? ALL_PRODUCTS_SCOPE,
    String(change.value)
  );
  if (change.hidden === above) return null;
  return change.hidden ? "hidden" : "shown";
}

/** The tile order of one releases set, or null when the set uses the product's order. */
export function setBackgroundOrderOf(
  settings: ChannelSettingsLike,
  type: string | null | undefined,
  product: string | null | undefined,
  set: string | null | undefined
): number[] | null {
  if (!type || !product || !set) return null;
  const order = settings?.setBackgroundOrders?.[setBackgroundOrderKey(type, product, set)];
  return order && order.length > 0 ? order : null;
}

/**
 * `items` in the order of a set. The items that the set order does not name
 * go first, in their own order: a background added after the set order was
 * saved leads, as a new background leads the product's list. Then the named
 * ones follow, in the set order. No set order: `items` unchanged.
 */
export function orderedBySet<Item>(
  items: Item[],
  variationOf: (item: Item) => number,
  setOrder: number[] | null | undefined
): Item[] {
  if (!setOrder || setOrder.length === 0) return items;
  const rank = new Map(setOrder.map((variation, index) => [variation, index]));
  const unnamed = items.filter((item) => !rank.has(variationOf(item)));
  const named = items
    .filter((item) => rank.has(variationOf(item)))
    .sort((a, b) => rank.get(variationOf(a))! - rank.get(variationOf(b))!);
  return [...unnamed, ...named];
}

/** `order` with `from` moved to the place of `to`, as a drag drops a tile. */
export function movedVariation(order: number[], from: number, to: number): number[] {
  const fromIndex = order.indexOf(from);
  const toIndex = order.indexOf(to);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return order;
  const next = order.filter((variation) => variation !== from);
  next.splice(toIndex, 0, from);
  return next;
}

/** The backgrounds hidden under one key: the catalog and the built-in ones. */
function hiddenBackgroundsAt(settings: ChannelSettingsLike, key: string): BackgroundId[] {
  return [
    ...(settings?.hiddenBackgrounds?.[key] ?? []),
    ...(settings?.hiddenBuiltInBackgrounds?.[key] ?? []).filter(isBuiltInBackground),
  ];
}

/**
 * The hidden backgrounds of the gallery of (type, product) on a page of
 * `set`: the ones hidden for every set, and the ones hidden for this set. A
 * page with no set reads the first list only.
 */
export function hiddenBackgroundsOf(
  settings: ChannelSettingsLike,
  type: string | null | undefined,
  product: string | null | undefined,
  set?: string | null
): Set<BackgroundId> {
  if (!type || !product) return new Set();
  return new Set([
    ...hiddenBackgroundsAt(settings, hiddenBackgroundsKey(type, product)),
    ...(set ? hiddenBackgroundsAt(settings, hiddenBackgroundsKey(type, product, set)) : []),
  ]);
}

/** The backgrounds hidden in one set only, without the ones hidden for every set. */
export function hiddenBackgroundsInSet(
  settings: ChannelSettingsLike,
  type: string,
  product: string,
  set: string
): Set<BackgroundId> {
  return new Set(hiddenBackgroundsAt(settings, hiddenBackgroundsKey(type, product, set)));
}

/**
 * The layout ids hidden on a page of (type, product, set): the ones hidden for
 * every product, and the ones hidden for this product.
 */
export function hiddenLayoutsOf(
  settings: ChannelSettingsLike,
  type: string | null | undefined,
  product: string | null | undefined,
  set?: string | null
): Set<number> {
  if (!type) return new Set();
  const lists = settings?.hiddenLayouts ?? {};
  return new Set([
    ...(lists[hiddenLayoutsKey(type, ALL_PRODUCTS_SCOPE, set)] ?? []),
    ...(product ? (lists[hiddenLayoutsKey(type, product, set)] ?? []) : []),
  ]);
}

/**
 * The layout buttons to show: `options` without the hidden ones. The current
 * layout stays, so a link to a hidden layout still shows which one is on. When
 * an admin hid every layout, all of them stay: a page must offer one.
 */
export function offeredLayoutOptions<Option extends {id: number}>(
  options: Option[],
  hidden: Set<number>,
  current?: number | null
): Option[] {
  if (hidden.size === 0) return options;
  if (options.every((option) => hidden.has(option.id))) return options;
  return options.filter((option) => !hidden.has(option.id) || option.id === current);
}

/**
 * The layout that code must select for the user, from `candidate`: the
 * candidate when the templates draw it and it is not hidden, else the first
 * offered layout. When every layout is hidden, the rule of getAllowedLayoutId
 * applies (the candidate, else the first layout).
 */
export function offeredLayoutId(
  candidate: number,
  options: Array<{id: number}>,
  hidden: Set<number>
): number {
  const isDrawn = (id: number) => options.some((option) => option.id === id);
  if (isDrawn(candidate) && !hidden.has(candidate)) return candidate;
  const first = options.find((option) => !hidden.has(option.id));
  if (first) return first.id;
  return isDrawn(candidate) ? candidate : (options[0]?.id ?? 1);
}

/**
 * The layout order of a page of (product, set): the product's own order, else
 * the order for every product. Product `*` reads the order for every product
 * only. Null when an admin saved no order: the code order applies, and the
 * declared default layout.
 */
export function layoutOrderOf(
  settings: ChannelSettingsLike,
  product: string | null | undefined,
  set?: string | null
): number[] | null {
  const orders = settings?.layoutOrders ?? {};
  const keys = [layoutOrderKey(ALL_PRODUCTS_SCOPE, set)];
  if (product && product !== ALL_PRODUCTS_SCOPE) keys.unshift(layoutOrderKey(product, set));
  for (const key of keys) {
    const order = orders[key];
    if (order && order.length > 0) return order;
  }
  return null;
}

/**
 * `options` in the layout order. The layouts that the order does not name
 * follow, in their code order: a layout added to the templates later does not
 * become the default. No order: `options` unchanged.
 */
export function orderedLayoutOptions<Option extends {id: number}>(
  options: Option[],
  order: number[] | null | undefined
): Option[] {
  if (!order || order.length === 0) return options;
  const rank = new Map(order.map((id, index) => [id, index]));
  const named = options
    .filter((option) => rank.has(option.id))
    .sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
  return [...named, ...options.filter((option) => !rank.has(option.id))];
}

/**
 * The default layout of a page: the layout that code selects for the user.
 * `options` are the layouts the templates draw, in the page's order. When an
 * admin saved an order, the first offered layout is the default. Else the
 * code's default (`declared`) is, when it is offered (offeredLayoutId).
 */
export function defaultLayoutId(
  declared: number,
  options: Array<{id: number}>,
  hidden: Set<number>,
  order: number[] | null | undefined
): number {
  const candidate = order && order.length > 0 && options.length > 0 ? options[0].id : declared;
  return offeredLayoutId(candidate, options, hidden);
}

/** `options` without the hidden ones, in their order. */
export function withoutHidden<Option extends {value: string}>(
  options: Option[],
  hidden: Set<string>
): Option[] {
  if (hidden.size === 0) return options;
  return options.filter((option) => !hidden.has(option.value));
}

/**
 * The product a channel opens on. The declared default when it is visible,
 * else the first visible product of `candidates` (the dropdown order). When
 * every candidate is hidden, the declared default stays: a landing must open
 * something.
 */
export function landingProduct(
  declared: string | null,
  candidates: string[],
  hidden: Set<string>
): string | null {
  if (declared === null || !hidden.has(declared)) return declared;
  return candidates.find((code) => !hidden.has(code)) ?? declared;
}

/**
 * The `set` URL param that a landing on a product needs. No param opens the
 * product's first set, so the param is necessary only when that set is
 * hidden: then it names the first visible set. Null when no param is
 * necessary, and when every set is hidden.
 */
export function landingSetParam(offeredSets: string[], hidden: Set<string>): string | null {
  if (offeredSets.length === 0 || !hidden.has(offeredSets[0])) return null;
  return offeredSets.find((value) => !hidden.has(value)) ?? null;
}

/** One hide/show request of PATCH /api/v3/channel-settings/hidden. */
export interface HiddenChange {
  channel: string;
  kind: "product" | "set" | "background" | "layout";
  /**
   * The product code, the set value, the background (a variation or a
   * built-in background), or the layout id. A product change with a type can
   * have `*`: the stage itself.
   */
  value: string | number;
  hidden: boolean;
  /**
   * The gallery of a background, or the page of a layout: its content type
   * and product. A layout can have product `*` (every product). A product or
   * a set change with a type is for that stage only (no type: every stage);
   * a set change has the product of its rule, `*` for every product.
   */
  type?: string;
  product?: string;
  /** Releases only: the set the change is for. No set = every set (backgrounds). */
  set?: string;
}

function toggled<T>(list: T[] | undefined, element: T, hidden: boolean): T[] {
  const rest = (list ?? []).filter((item) => item !== element);
  return hidden ? [...rest, element] : rest;
}

/**
 * The settings after `change`, as the server will store them. The Defaults
 * panel shows this at once, before the request completes.
 */
export function withHiddenChange<Settings extends Partial<HiddenSelectorOptions>>(
  settings: Settings,
  change: HiddenChange
): Settings {
  if (change.kind === "product") {
    if (change.type) {
      const mark = stageProductMarkAfter(change);
      const value = String(change.value);
      const hiddenLists = settings.hiddenStageProducts ?? {};
      const shownLists = settings.shownStageProducts ?? {};
      return {
        ...settings,
        hiddenStageProducts: {
          ...hiddenLists,
          [change.type]: toggled(hiddenLists[change.type], value, mark === "hidden"),
        },
        shownStageProducts: {
          ...shownLists,
          [change.type]: toggled(shownLists[change.type], value, mark === "shown"),
        },
      };
    }
    return {
      ...settings,
      hiddenProducts: toggled(settings.hiddenProducts, String(change.value), change.hidden),
    };
  }
  if (change.kind === "set") {
    if (change.type) {
      const key = hiddenStageSetsKey(change.type, change.product ?? ALL_PRODUCTS_SCOPE);
      const mark = stageSetMarkAfter(settings, change);
      const value = String(change.value);
      const hiddenLists = settings.hiddenStageSets ?? {};
      const shownLists = settings.shownStageSets ?? {};
      return {
        ...settings,
        hiddenStageSets: {...hiddenLists, [key]: toggled(hiddenLists[key], value, mark === "hidden")},
        shownStageSets: {...shownLists, [key]: toggled(shownLists[key], value, mark === "shown")},
      };
    }
    return {
      ...settings,
      hiddenSets: toggled(settings.hiddenSets, String(change.value), change.hidden),
    };
  }
  if (change.kind === "layout") {
    if (!change.type || !change.product) {
      throw new Error("A layout change needs the type and the product scope of its page");
    }
    const key = hiddenLayoutsKey(change.type, change.product, change.set);
    const layouts = settings.hiddenLayouts ?? {};
    return {
      ...settings,
      hiddenLayouts: {
        ...layouts,
        [key]: toggled(layouts[key], Number(change.value), change.hidden),
      },
    };
  }
  if (!change.type || !change.product) {
    throw new Error("A background change needs the type and the product of its gallery");
  }
  const key = hiddenBackgroundsKey(change.type, change.product, change.set);
  if (isBuiltInBackground(change.value)) {
    const builtIn = settings.hiddenBuiltInBackgrounds ?? {};
    return {
      ...settings,
      hiddenBuiltInBackgrounds: {
        ...builtIn,
        [key]: toggled(builtIn[key], change.value, change.hidden),
      },
    };
  }
  const backgrounds = settings.hiddenBackgrounds ?? {};
  return {
    ...settings,
    hiddenBackgrounds: {
      ...backgrounds,
      [key]: toggled(backgrounds[key], Number(change.value), change.hidden),
    },
  };
}

/**
 * True when a page offers a tile for the background that `?background=`
 * names. An upload ("custom") is no background of the gallery, and no hidden
 * list applies to it.
 */
export function isBackgroundOffered(
  background: string,
  hidden: ReadonlySet<BackgroundId>
): boolean {
  return !hidden.has(isBuiltInBackground(background) ? background : Number(background));
}

/**
 * The first variation of `order` that has a tile, for code that must select
 * a background for the user. Falls back to the first entry when every one is
 * hidden.
 */
export function firstVisibleVariation(
  order: number[],
  hidden: ReadonlySet<BackgroundId>
): number | undefined {
  return order.find((variation) => !hidden.has(variation)) ?? order[0];
}
